import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../utils/Auth';
import API from '../../API';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import LogTableDisplay from '../../components/LogTableDisplay';
import styles from './SurveyPage.module.css';

export default function SurveyPage() {
    const { getQRListener, updateLogToDeleted, getLogsListener } = API;
    const { activeOrgId, isLoading } = useAuth();
    const navigate = useNavigate();
    
    // ----- STATE INITIALIZATION -----
    const [ qrObj, setQrObj ] = useState(null);
    const [ editResponse, setEditResponse ] = useState(false);
    const [ deleteResponse, setDeleteResponse ] = useState(false);
    const [ selectedResponse, setSelectedResponse ] = useState(null);
    const [ deletedToggle, setDeletedToggle ] = useState(false);
    const [ logs, setLogs ] = useState([]);
    const [ showAddLog, setShowAddLog ] = useState(false);
    const authenticatedUser = useMemo(() => activeOrgId === qrObj?.org_id, [activeOrgId, qrObj?.org_id]);
    const isCheckingAuth = useMemo(() => isLoading || !qrObj, [isLoading, qrObj]);

    const { id } = useParams();
    // ----- END STATE INITIALIZATION -----
    // ----------------------------------------------

    useEffect(() => {
        let listener,logsListener;

        if (id) {
            listener = getQRListener(id, setQrObj);
            logsListener = getLogsListener('survey', id, setLogs);
        };

        return () => {
            listener && listener();
            logsListener && logsListener();
            setQrObj(null);
            setLogs([]);
        };
    }, []);

    useEffect(() => {
        if (!qrObj?.qr_type) return;

        if (!['survey','review','contact-form'].includes(qrObj?.qr_type || '')) {
            if (['tracker','workout-log','service-log','vehicle-log','maintenance-tracker'].includes(qrObj?.qr_type || '')) {
                navigate(`/t/${id}`);
            } else {
                navigate('/notfound');
            };
        };
    }, [qrObj]);

    const handleDeleteResponse = async () => {
        try {
            const res = await updateLogToDeleted(id, selectedResponse.date, 'survey');
            if (res === true) {
                setSelectedResponse(null);
                setEditResponse(false);
                setDeleteResponse(false);
            };
        } catch (err) {
            console.error(err);
        };
    };

    const handleNavButtons = () => {
        if (!selectedResponse && !showAddLog) {
            return (
                <div className={styles.navBtnCont}>
                    <button className={styles.backBtn} onClick={() => navigate(`/collection`, { state: { qrID: id } }) } >Collection</button>
                </div>
            );
        };

        if (showAddLog && authenticatedUser) {
            return (
                <div className={styles.navBtnCont}>
                    <button className={styles.backBtn} onClick={() => setShowAddLog(false)} >Back</button>
                </div>
            );
        };

        if (deletedToggle) {
            return (
                <div className={styles.navBtnCont}>
                    <button className={styles.backBtn} onClick={() => setSelectedResponse(null)} >Back</button>
                </div>
            );
        } else if (editResponse) {
            return (
                <div className={styles.navBtnCont}>
                    <button className={styles.backBtn} onClick={() => setEditResponse(false)} >Back</button>
                </div>
            );
        } else if (deleteResponse) {
            return (
                <>
                    <div className={styles.navBtnCont}>
                        <button className={styles.cancelDeleteBtn} onClick={() => setDeleteResponse(false)} >Cancel</button>
                        <button className={styles.deleteBtn} onClick={handleDeleteResponse} >Confirm</button>
                    </div>
                    <p className={styles.deleteText}>Deleting responses is permanent. Delete response?</p>
                </>
            );
        } else {
            return (
                <div className={styles.navBtnCont}>
                    <button className={styles.backBtn} onClick={() => setSelectedResponse(null)}>Back</button>
                    <button className={styles.deleteBtn} onClick={() => setDeleteResponse(true)} >Delete</button>
                </div>
            );
        };
    };

    if (!qrObj || !qrObj?.survey || !['survey','review','contact-form']?.includes(qrObj?.qr_type || '')) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.container}>
                    <LoadingSpinner />
                </div>
            </div>
        );
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <div className={styles.display}>
                    <div className={styles.navContainer}>
                        {authenticatedUser ?
                            handleNavButtons()
                        : selectedResponse ?
                            <div className={styles.navBtnCont}>
                                <button className={styles.backBtn} onClick={() => setSelectedResponse(null)} >Back</button>
                            </div>
                        :
                            <></>
                        }
                        {authenticatedUser ? ((selectedResponse) ? <button className={styles.editBtn} onClick={() => setEditResponse(true)} disabled={selectedResponse?.deleted} >Edit</button> : <button className={styles.editBtn} onClick={() => navigate(`/collection/${id}/edit?tab=configure`)}>Edit</button>) : <></>}
                    </div>
                    <div className={styles.content}>
                        <LogTableDisplay
                            id={id}
                            qrType={'survey'}
                            qrObj={qrObj}
                            qrData={qrObj?.survey}
                            logs={logs || []}
                            editLogEntry={editResponse}
                            setEditLogEntry={setEditResponse}
                            selectedLog={selectedResponse}
                            setSelectedLog={setSelectedResponse}
                            authenticatedUser={authenticatedUser}
                            isCheckingAuth={isCheckingAuth}
                            deletedToggle={deletedToggle}
                            setDeletedToggle={setDeletedToggle}
                            onShowAddLogChange={setShowAddLog}
                            externalShowAddLog={showAddLog}
                            externalSetShowAddLog={setShowAddLog}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
